<script setup lang="ts">
  import { VRadio } from "@krainovsd/vue-ui";
  import { computed, ref } from "vue";
  import { useI18n } from "vue-i18n";
  import { ATTRIBUTION_OPTIONS } from "@/entities/report/report.constants";
  import Card from "@/components/atoms/Card.vue";
  import StepLayout from "@/components/molecules/constructor/StepLayout.vue";

  const { t } = useI18n();

  const selectedKey = defineModel<number>({ required: true });
  const emit = defineEmits<{ next: [] }>();

  const layoutRef = ref<{ rootEl: HTMLElement | null } | null>(null);
  defineExpose({
    rootEl: computed(() => layoutRef.value?.rootEl ?? null),
  });

  const attributions = computed(() =>
    ATTRIBUTION_OPTIONS.map((option) => ({
      ...option,
      title: t(option.title.path, {}, { default: option.title.default }),
      subtitle: t(option.subtitle.path, {}, { default: option.subtitle.default }),
      description: t(option.description.path, {}, { default: option.description.default }),
    })),
  );
</script>

<template>
  <StepLayout
    ref="layoutRef"
    :title="$t('page.createReport.step1.title', {}, { default: 'Режим атрибуции' })"
    next
    @next="emit('next')"
  >
    <div :class="$style.attribution">
      <Card
        v-for="option in attributions"
        :key="option.key"
        :class="$style.attributionCard"
        @click="selectedKey = option.key"
      >
        <VRadio
          v-model="selectedKey"
          :name="option.title"
          :value="option.key"
          :class="$style.attributionRadio"
        />
        <div :class="$style.attributionContent">
          <div :class="$style.attributionHeader">
            <span :class="$style.attributionTitle">{{ option.title }}</span>
            <span :class="$style.attributionSubtitle">{{ option.subtitle }}</span>
          </div>
          <div :class="$style.attributionDescription">{{ option.description }}</div>
        </div>
      </Card>
    </div>
  </StepLayout>
</template>

<style lang="scss" module>
  .attribution {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .attributionCard {
    padding: 14px;
    display: flex;
    align-items: flex-start;

    cursor: pointer;
  }

  .attributionContent {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .attributionRadio {
    margin-top: 4px;
  }

  .attributionHeader {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .attributionTitle {
    color: var(--foreground);
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .attributionSubtitle {
    color: var(--muted-foreground);
    font-size: 0.75rem;
    line-height: 1rem;
  }

  .attributionDescription {
    color: var(--muted-foreground);
    font-size: 13px;
    line-height: 1.375;
  }
</style>
